import { useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { printPage } from "../lib/print";
import { downloadFromApi } from "../lib/download";

const todayLocal = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const yesterdayLocal = () => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

export default function StaffFilesPage() {
  const { isAdmin } = useAuth();
  const [searchParams] = useSearchParams();
  const [accidentReports, setAccidentReports] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReport, setSelectedReport] = useState(null);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [staffFilter, setStaffFilter] = useState("all");
  const [staffStatusFilter, setStaffStatusFilter] = useState("current");
  const [staffDocuments, setStaffDocuments] = useState([]);
  const [editingStaff, setEditingStaff] = useState(false);
  const [editStaffData, setEditStaffData] = useState({});
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [docType, setDocType] = useState("teaching_license");
  const fileInputRef = useRef(null);
  const staffImportRef = useRef(null);
  const [pdDialogOpen, setPdDialogOpen] = useState(false);
  const [pdStaffIds, setPdStaffIds] = useState([]);
  const [pdDate, setPdDate] = useState(todayLocal());
  const [gapDate, setGapDate] = useState(yesterdayLocal());
  const [gaps, setGaps] = useState([]);
  const [gapsMeta, setGapsMeta] = useState({ is_school_day: true, no_school_reason: "" });
  const [gapsLoading, setGapsLoading] = useState(false);

  const handleStaffExport = async () => {
    try {
      toast.info("Generating staff export...");
      await downloadFromApi("/api/staff/export", "staff_directory_export.xlsx");
      toast.success("Staff directory exported!");
    } catch { toast.error("Failed to export staff data"); }
  };

  const handleStaffImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      toast.info("Importing staff updates...");
      const token = localStorage.getItem("token");
      const baseUrl = process.env.REACT_APP_BACKEND_URL;
      const formData = new FormData();
      formData.append("file", file);
      const response = await fetch(`${baseUrl}/api/staff/import`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.detail || "Import failed");
      toast.success(`Import complete: ${result.updated} staff updated`);
      if (result.errors?.length > 0) {
        toast.error(`${result.errors.length} errors during import`);
      }
      refreshStaff();
    } catch (err) { toast.error(err.message || "Failed to import staff data"); }
    e.target.value = "";
  };

  const handleStaffOneSheets = async () => {
    try {
      toast.info("Generating staff one sheets PDF...");
      await downloadFromApi("/api/staff/print/one-sheets", "staff_one_sheets.pdf");
      toast.success("Staff one sheets PDF downloaded!");
    } catch { toast.error("Failed to generate staff one sheets"); }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Deep-link: ?staff=<id> (e.g. from global search) auto-opens that staff file.
  useEffect(() => {
    const staffId = searchParams.get("staff");
    if (staffId && staffList.length > 0 && selectedStaff?.id !== staffId) {
      const match = staffList.find((s) => s.id === staffId);
      if (match) { setStaffStatusFilter("all"); handleSelectStaff(match); }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staffList, searchParams]);

  const fetchGaps = async (date) => {
    setGapsLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/assessment-gaps?date=${date}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setGaps(data.gaps || []);
        setGapsMeta({ is_school_day: data.is_school_day, no_school_reason: data.no_school_reason || "" });
      }
    } catch (err) {
      toast.error("Failed to load assessment gaps");
    } finally {
      setGapsLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin()) fetchGaps(gapDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gapDate]);

  const shiftGapDate = (delta) => {
    const d = new Date(gapDate + "T00:00:00");
    d.setDate(d.getDate() + delta);
    setGapDate(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`);
  };
  const gapPrettyDate = new Date(gapDate + "T00:00:00").toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric", year: "numeric" });

  const fetchData = async () => {
    try {
      const token = localStorage.getItem("token");
      
      // Fetch staff list
      const staffRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/staff`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (staffRes.ok) {
        const staff = await staffRes.json();
        setStaffList(staff.sort((a, b) => a.name.localeCompare(b.name)));
      }
      
      // Fetch staff accident reports
      const reportsRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/accident-reports/staff`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (reportsRes.ok) {
        const reports = await reportsRes.json();
        setAccidentReports(reports);
      }
    } catch (err) {
      toast.error("Failed to load staff files");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStaffStatus = async (staffId, currentStatus) => {
    const isFormer = currentStatus === "former";
    const action = isFormer ? "reinstate" : "mark as former";
    if (!window.confirm(`Are you sure you want to ${action} this staff member?`)) return;
    try {
      const token = localStorage.getItem("token");
      const newStatus = isFormer ? "current" : "former";
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/staff/${staffId}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ status: newStatus }),
      });
      if (res.ok) {
        toast.success(isFormer ? "Staff member reinstated" : "Staff member marked as former");
        fetchData();
        setSelectedStaff((prev) => prev ? { ...prev, status: newStatus } : null);
      } else {
        toast.error("Failed to update staff status");
      }
    } catch {
      toast.error("Failed to update staff status");
    }
  };

  const fetchStaffDocuments = async (staffId) => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/staff/${staffId}/documents`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const docs = await res.json();
        setStaffDocuments(docs);
      }
    } catch (err) {
      console.error("Failed to load documents");
    }
  };

  const refreshStaff = async () => {
    const token = localStorage.getItem("token");
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/staff`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.ok) {
      const staff = await res.json();
      const sorted = staff.sort((a, b) => a.name.localeCompare(b.name));
      setStaffList(sorted);
      setSelectedStaff((prev) => (prev ? sorted.find((s) => s.id === prev.id) || prev : prev));
    }
  };

  const openPersonalDayDialog = () => {
    setPdStaffIds(selectedStaff ? [selectedStaff.id] : []);
    setPdDate(todayLocal());
    setPdDialogOpen(true);
  };

  const handleAddPersonalDay = async () => {
    if (pdStaffIds.length === 0) { toast.error("Select at least one staff member"); return; }
    if (!pdDate) { toast.error("Pick a date"); return; }
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/staff/personal-days`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ staff_ids: pdStaffIds, date: pdDate }),
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || "Failed"); }
      const data = await res.json();
      toast.success(`Personal day recorded for ${data.updated} staff member(s)`);
      setPdDialogOpen(false);
      setPdStaffIds([]);
      setPdDate(todayLocal());
      await refreshStaff();
    } catch (err) { toast.error(err.message || "Failed to record personal day"); }
  };

  const handleRemovePersonalDay = async (staffId, day) => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/staff/${staffId}/personal-days/${day}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed");
      toast.success("Personal day removed");
      await refreshStaff();
    } catch { toast.error("Failed to remove personal day"); }
  };

  const handleDeleteStaff = async (staffId) => {
    if (!confirm("Are you sure you want to permanently delete this staff member? This cannot be undone.")) return;
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/staff/${staffId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        toast.success("Staff member deleted");
        setSelectedStaff(null);
        setStaffDocuments([]);
        fetchData();
      } else {
        const data = await res.json().catch(() => ({}));
        toast.error(data.detail || "Failed to delete staff member");
      }
    } catch (err) {
      toast.error("Failed to delete staff member");
    }
  };

  const handleSelectStaff = async (staff) => {
    setSelectedStaff(staff);
    setEditStaffData(staff);
    setEditingStaff(false);
    await fetchStaffDocuments(staff.id);
  };

  const handleUpdateStaff = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/staff/${selectedStaff.id}`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(editStaffData),
      });
      
      if (res.ok) {
        toast.success("Staff information updated");
        setEditingStaff(false);
        fetchData();
        setSelectedStaff({ ...selectedStaff, ...editStaffData });
      } else {
        toast.error("Failed to update staff");
      }
    } catch (err) {
      toast.error("Failed to update staff");
    }
  };

  const handleUploadDocument = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploadingDoc(true);
    try {
      const token = localStorage.getItem("token");
      const formData = new FormData();
      formData.append("file", file);
      formData.append("document_type", docType);
      
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/staff/${selectedStaff.id}/documents`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      
      if (res.ok) {
        toast.success("Document uploaded successfully");
        fetchStaffDocuments(selectedStaff.id);
      } else {
        toast.error("Failed to upload document");
      }
    } catch (err) {
      toast.error("Failed to upload document");
    } finally {
      setUploadingDoc(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (!confirm("Are you sure you want to delete this document?")) return;
    
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/staff/${selectedStaff.id}/documents/${docId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (res.ok) {
        toast.success("Document deleted");
        fetchStaffDocuments(selectedStaff.id);
      }
    } catch (err) {
      toast.error("Failed to delete document");
    }
  };

  const handleDeleteReport = async (reportId) => {
    if (!confirm("Are you sure you want to delete this accident report? This cannot be undone.")) return;

    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/accident-reports/${reportId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.ok) {
        toast.success("Accident report deleted");
        setAccidentReports((prev) => prev.filter((r) => r.id !== reportId));
      } else {
        toast.error("Failed to delete accident report");
      }
    } catch (err) {
      toast.error("Failed to delete accident report");
    }
  };

  const handleViewDocument = async (doc) => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/staff/${selectedStaff.id}/documents/${doc.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (res.ok) {
        const data = await res.json();
        // Convert base64 to blob and open
        const byteCharacters = atob(data.file_data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: data.content_type });
        const url = window.URL.createObjectURL(blob);
        window.open(url, "_blank");
      }
    } catch (err) {
      toast.error("Failed to view document");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const d = dateStr.includes('T') ? new Date(dateStr) : new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const formatMedicalResponse = (response) => {
    const labels = { first_aid: "First Aid", ambulance: "Ambulance", na: "N/A" };
    return labels[response] || response;
  };

  const getDocTypeLabel = (type) => {
    const labels = {
      teaching_license: "Teaching License",
      drivers_license: "Driver's License",
      hiring_paperwork: "Hiring Paperwork",
      other: "Other Document",
    };
    return labels[type] || type;
  };

  const handlePrintReport = (report) => {
    const content = `
      <div class="grid-2">
        <div><div class="field-label">Staff Member</div><div class="field-value">${report.subject_name}</div></div>
        <div><div class="field-label">Medical Response</div><div class="field-value">${formatMedicalResponse(report.medical_response)}</div></div>
        <div><div class="field-label">Date</div><div class="field-value">${formatDate(report.date)}</div></div>
        <div><div class="field-label">Time</div><div class="field-value">${report.time || "-"}</div></div>
        <div><div class="field-label">Place</div><div class="field-value">${report.place_of_accident}</div></div>
        <div><div class="field-label">Witness</div><div class="field-value">${report.witness_name}</div></div>
      </div>
      <div class="section"><div class="section-title">Incident Description</div><div class="section-content">${report.describe_incident}</div></div>
      <div class="section"><div class="section-title">Injury Description</div><div class="section-content">${report.describe_injury}</div></div>
      ${report.follow_up ? `<div class="section"><div class="section-title">Follow-up</div><div class="section-content">${report.follow_up}</div></div>` : ""}
      <div class="print-footer"><p>Report Created: ${formatDate(report.created_at)}</p></div>
    `;

    printPage({
      title: "Staff Accident Report",
      subtitle: "SMARTIS — Confidential",
      content,
      accentColor: "#A62E1D",
      extraStyles: `body { max-width: 800px; margin: 0 auto; padding: 40px; }`,
    });
  };

  const filteredReports = staffFilter === "all" 
    ? accidentReports 
    : accidentReports.filter(r => r.subject_name === staffFilter);

  if (!isAdmin()) {
    return (
      <div className="flex items-center justify-center h-64">
        <Card className="max-w-md">
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="text-6xl mb-4">🔒</div>
              <h2 className="text-xl font-semibold text-[#00305E] mb-2">Access Restricted</h2>
              <p className="text-muted-foreground">Only administrators can access staff files.</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="staff-files-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-[#00305E]">Staff Files</h1>
          <p className="text-muted-foreground">Manage staff records, documents, and accident reports</p>
        </div>
        {isAdmin() && (
          <div className="flex gap-2">
            <input type="file" ref={staffImportRef} className="hidden" accept=".xlsx" onChange={handleStaffImport} />
            <Button variant="outline" size="sm" onClick={openPersonalDayDialog} data-testid="add-personal-day-btn">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-1"><rect width="18" height="18" x="3" y="4" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/><line x1="12" y1="14" x2="12" y2="18"/><line x1="10" y1="16" x2="14" y2="16"/></svg>
              Add Personal Day
            </Button>
            <Button variant="outline" size="sm" onClick={handleStaffExport} data-testid="staff-export-btn">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-1"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
              Export Staff
            </Button>
            <Button variant="outline" size="sm" onClick={() => staffImportRef.current?.click()} data-testid="staff-import-btn">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-1"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
              Import Updates
            </Button>
            <Button variant="outline" size="sm" onClick={handleStaffOneSheets} data-testid="staff-one-sheets-btn">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-1"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect width="12" height="8" x="6" y="14"/></svg>
              One Sheets
            </Button>
          </div>
        )}
      </div>

      {/* Add Personal Day Dialog */}
      <Dialog open={pdDialogOpen} onOpenChange={setPdDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Add Personal Day</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label>Date</Label>
              <Input type="date" value={pdDate} onChange={(e) => setPdDate(e.target.value)} data-testid="personal-day-date" />
              <p className="text-xs text-muted-foreground">{"Defaults to today — change it if you're logging a day you missed."}</p>
            </div>
            <div className="space-y-2">
              <Label>Staff ({pdStaffIds.length} selected)</Label>
              <div className="border rounded-md max-h-64 overflow-y-auto divide-y">
                {staffList.filter((s) => s.status !== "former").map((s) => (
                  <label key={s.id} className="flex items-center gap-2 p-2 hover:bg-muted cursor-pointer text-sm">
                    <input
                      type="checkbox"
                      checked={pdStaffIds.includes(s.id)}
                      onChange={() => setPdStaffIds((prev) => prev.includes(s.id) ? prev.filter((x) => x !== s.id) : [...prev, s.id])}
                      data-testid={`personal-day-staff-${s.id}`}
                    />
                    <span>{s.name}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setPdDialogOpen(false)}>Cancel</Button>
              <Button className="bg-[#00305E]" onClick={handleAddPersonalDay} data-testid="save-personal-day-btn">Save</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Tabs defaultValue="records" className="space-y-6">
        <TabsList>
          <TabsTrigger value="records" data-testid="tab-staff-records">Staff Records</TabsTrigger>
          <TabsTrigger value="accidents" data-testid="tab-accidents">Accident Reports</TabsTrigger>
          {isAdmin() && <TabsTrigger value="gaps" data-testid="tab-gaps">Gaps</TabsTrigger>}
        </TabsList>

        {/* STAFF RECORDS TAB */}
        <TabsContent value="records" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
                  <circle cx="9" cy="7" r="4"/>
                  <path d="M22 21v-2a4 4 0 0 0-3-3.87"/>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                </svg>
                Staff Directory
              </CardTitle>
              <CardDescription>Select a staff member to view and manage their records</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Staff List */}
                <div className="md:col-span-1 border rounded-lg p-4 max-h-[600px] overflow-y-auto">
                  <h3 className="font-semibold mb-3 text-sm text-muted-foreground uppercase">Staff Members</h3>
                  <div className="flex gap-1 mb-3">
                    {["current", "former", "all"].map((s) => (
                      <button
                        key={s}
                        onClick={() => setStaffStatusFilter(s)}
                        className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                          staffStatusFilter === s
                            ? "bg-[#00305E] text-white"
                            : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                        }`}
                        data-testid={`staff-status-filter-${s}`}
                      >
                        {s.charAt(0).toUpperCase() + s.slice(1)}
                      </button>
                    ))}
                  </div>
                  <div className="space-y-2">
                    {staffList
                      .filter((s) => {
                        if (staffStatusFilter === "current") return s.status !== "former";
                        if (staffStatusFilter === "former") return s.status === "former";
                        return true;
                      })
                      .map((staff) => (
                      <div
                        key={staff.id}
                        onClick={() => handleSelectStaff(staff)}
                        className={`p-3 rounded-lg cursor-pointer transition-colors ${
                          selectedStaff?.id === staff.id 
                            ? "bg-[#00305E] text-white" 
                            : "hover:bg-muted"
                        }`}
                        data-testid={`staff-item-${staff.id}`}
                      >
                        <div className="flex items-center gap-2">
                          <p className="font-medium">{staff.name}</p>
                          {staff.status === "former" && (
                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                              selectedStaff?.id === staff.id ? "bg-white/20 text-white" : "bg-gray-200 text-gray-600"
                            }`}>Former</span>
                          )}
                        </div>
                        <p className={`text-sm ${selectedStaff?.id === staff.id ? "text-white/70" : "text-muted-foreground"}`}>
                          {staff.positions?.join(", ") || "No position"}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Staff Details */}
                <div className="md:col-span-2">
                  {selectedStaff ? (
                    <div className="space-y-6">
                      {/* Header */}
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="flex items-center gap-2">
                            <h2 className="text-xl font-bold text-[#00305E]">{selectedStaff.name}</h2>
                            {selectedStaff.status === "former" && (
                              <span className="px-2 py-0.5 rounded bg-gray-200 text-gray-600 text-sm font-medium">Former Staff</span>
                            )}
                          </div>
                          <p className="text-muted-foreground">{selectedStaff.email}</p>
                        </div>
                        <div className="flex gap-2">
                          {isAdmin() && (
                            <>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleToggleStaffStatus(selectedStaff.id, selectedStaff.status)}
                              className={selectedStaff.status === "former" ? "text-green-700 border-green-300 hover:bg-green-50" : "text-orange-700 border-orange-300 hover:bg-orange-50"}
                              data-testid="toggle-staff-status-btn"
                            >
                              {selectedStaff.status === "former" ? "Reinstate" : "Mark as Former"}
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDeleteStaff(selectedStaff.id)}
                              className="text-red-700 border-red-300 hover:bg-red-50"
                              data-testid="delete-staff-btn"
                            >
                              Delete
                            </Button>
                            </>
                          )}
                          <Button
                            variant={editingStaff ? "outline" : "default"}
                            onClick={() => {
                              if (editingStaff) {
                                setEditStaffData(selectedStaff);
                              }
                              setEditingStaff(!editingStaff);
                            }}
                            className={!editingStaff ? "bg-[#00305E]" : ""}
                          >
                            {editingStaff ? "Cancel" : "Edit Info"}
                          </Button>
                        </div>
                      </div>

                      {/* Personal Information */}
                      <div className="border rounded-lg p-4">
                        <h3 className="font-semibold mb-4 flex items-center gap-2">
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="10"/>
                            <circle cx="12" cy="10" r="3"/>
                            <path d="M7 20.662V19a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v1.662"/>
                          </svg>
                          Personal Information
                        </h3>
                        
                        {editingStaff ? (
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label>Email</Label>
                              <Input
                                type="email"
                                value={editStaffData.email || ""}
                                onChange={(e) => setEditStaffData({...editStaffData, email: e.target.value})}
                                placeholder="staff@school.edu"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label>Birthday</Label>
                              <Input
                                type="date"
                                value={editStaffData.dob || ""}
                                onChange={(e) => setEditStaffData({...editStaffData, dob: e.target.value})}
                              />
                            </div>
                            <div className="space-y-2">
                              <Label>Hire Date</Label>
                              <Input
                                type="date"
                                value={editStaffData.hire_date || ""}
                                onChange={(e) => setEditStaffData({...editStaffData, hire_date: e.target.value})}
                              />
                            </div>
                            <div className="space-y-2">
                              <Label>Personal Phone</Label>
                              <Input
                                value={editStaffData.personal_phone || ""}
                                onChange={(e) => setEditStaffData({...editStaffData, personal_phone: e.target.value})}
                                placeholder="555-123-4567"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label>Work Phone</Label>
                              <Input
                                value={editStaffData.work_phone || ""}
                                onChange={(e) => setEditStaffData({...editStaffData, work_phone: e.target.value})}
                                placeholder="555-987-6543"
                              />
                            </div>
                            <div className="col-span-2 space-y-2">
                              <Label>Home Address</Label>
                              <Input
                                value={editStaffData.address || ""}
                                onChange={(e) => setEditStaffData({...editStaffData, address: e.target.value})}
                                placeholder="123 Main Street"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label>City</Label>
                              <Input
                                value={editStaffData.city || ""}
                                onChange={(e) => setEditStaffData({...editStaffData, city: e.target.value})}
                              />
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                              <div className="space-y-2">
                                <Label>State</Label>
                                <Input
                                  value={editStaffData.state || ""}
                                  onChange={(e) => setEditStaffData({...editStaffData, state: e.target.value})}
                                />
                              </div>
                              <div className="space-y-2">
                                <Label>ZIP</Label>
                                <Input
                                  value={editStaffData.zip_code || ""}
                                  onChange={(e) => setEditStaffData({...editStaffData, zip_code: e.target.value})}
                                />
                              </div>
                            </div>
                            <div className="space-y-2">
                              <Label>Emergency Contact Name</Label>
                              <Input
                                value={editStaffData.emergency_contact_name || ""}
                                onChange={(e) => setEditStaffData({...editStaffData, emergency_contact_name: e.target.value})}
                                placeholder="Contact name"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label>Emergency Contact Phone</Label>
                              <Input
                                value={editStaffData.emergency_contact_phone || ""}
                                onChange={(e) => setEditStaffData({...editStaffData, emergency_contact_phone: e.target.value})}
                                placeholder="555-111-2222"
                              />
                            </div>
                          </div>
                        ) : (
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <p className="text-muted-foreground">Birthday</p>
                              <p className="font-medium">{formatDate(selectedStaff.dob) || "-"}</p>
                            </div>
                            <div>
                              <p className="text-muted-foreground">Hire Date</p>
                              <p className="font-medium">{formatDate(selectedStaff.hire_date) || "-"}</p>
                            </div>
                            <div>
                              <p className="text-muted-foreground">Personal Phone</p>
                              <p className="font-medium">{selectedStaff.personal_phone || "-"}</p>
                            </div>
                            <div>
                              <p className="text-muted-foreground">Work Phone</p>
                              <p className="font-medium">{selectedStaff.work_phone || "-"}</p>
                            </div>
                            <div className="col-span-2">
                              <p className="text-muted-foreground">Home Address</p>
                              <p className="font-medium">
                                {selectedStaff.address ? (
                                  <>
                                    {selectedStaff.address}
                                    {selectedStaff.city && `, ${selectedStaff.city}`}
                                    {selectedStaff.state && `, ${selectedStaff.state}`}
                                    {selectedStaff.zip_code && ` ${selectedStaff.zip_code}`}
                                  </>
                                ) : "-"}
                              </p>
                            </div>
                            <div>
                              <p className="text-muted-foreground">Emergency Contact</p>
                              <p className="font-medium">{selectedStaff.emergency_contact_name || "-"}</p>
                            </div>
                            <div>
                              <p className="text-muted-foreground">Emergency Phone</p>
                              <p className="font-medium">{selectedStaff.emergency_contact_phone || "-"}</p>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Licenses */}
                      <div className="border rounded-lg p-4">
                        <h3 className="font-semibold mb-4 flex items-center gap-2">
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect width="20" height="14" x="2" y="5" rx="2"/>
                            <line x1="2" x2="22" y1="10" y2="10"/>
                          </svg>
                          Licenses & Credentials
                        </h3>
                        
                        {editingStaff ? (
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label>Teaching License #</Label>
                              <Input
                                value={editStaffData.teaching_license_number || ""}
                                onChange={(e) => setEditStaffData({...editStaffData, teaching_license_number: e.target.value})}
                              />
                            </div>
                            <div className="space-y-2">
                              <Label>License Expiry</Label>
                              <Input
                                type="date"
                                value={editStaffData.teaching_license_expiry || ""}
                                onChange={(e) => setEditStaffData({...editStaffData, teaching_license_expiry: e.target.value})}
                              />
                            </div>
                            <div className="space-y-2">
                              <Label>Driver's License #</Label>
                              <Input
                                value={editStaffData.drivers_license_number || ""}
                                onChange={(e) => setEditStaffData({...editStaffData, drivers_license_number: e.target.value})}
                              />
                            </div>
                            <div className="space-y-2">
                              <Label>License Expiry</Label>
                              <Input
                                type="date"
                                value={editStaffData.drivers_license_expiry || ""}
                                onChange={(e) => setEditStaffData({...editStaffData, drivers_license_expiry: e.target.value})}
                              />
                            </div>
                          </div>
                        ) : (
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <p className="text-muted-foreground">Teaching License</p>
                              <p className="font-medium">{selectedStaff.teaching_license_number || "-"}</p>
                              {selectedStaff.teaching_license_expiry && (
                                <p className="text-xs text-muted-foreground">Expires: {formatDate(selectedStaff.teaching_license_expiry)}</p>
                              )}
                            </div>
                            <div>
                              <p className="text-muted-foreground">Driver's License</p>
                              <p className="font-medium">{selectedStaff.drivers_license_number || "-"}</p>
                              {selectedStaff.drivers_license_expiry && (
                                <p className="text-xs text-muted-foreground">Expires: {formatDate(selectedStaff.drivers_license_expiry)}</p>
                              )}
                            </div>
                          </div>
                        )}
                      </div>

                      {editingStaff && (
                        <Button onClick={handleUpdateStaff} className="bg-[#00305E]">
                          Save Changes
                        </Button>
                      )}

                      {/* Personal Days Section */}
                      <div className="border rounded-lg p-4">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="font-semibold flex items-center gap-2">
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="18" height="18" x="3" y="4" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                            Personal Days
                          </h3>
                          {isAdmin() && (
                            <Button variant="outline" size="sm" onClick={openPersonalDayDialog} data-testid="staff-file-add-personal-day">+ Add</Button>
                          )}
                        </div>
                        {(selectedStaff.personal_days && selectedStaff.personal_days.length > 0) ? (
                          <div className="flex flex-wrap gap-2" data-testid="personal-days-list">
                            {[...selectedStaff.personal_days].sort().reverse().map((day) => (
                              <span key={day} className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 text-[#00305E] text-sm border border-blue-100" data-testid={`personal-day-${day}`}>
                                {formatDate(day)}
                                {isAdmin() && (
                                  <button onClick={() => handleRemovePersonalDay(selectedStaff.id, day)} className="text-red-500 hover:text-red-700 font-bold leading-none" title="Remove" data-testid={`remove-personal-day-${day}`}>×</button>
                                )}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-muted-foreground">No personal days recorded.</p>
                        )}
                        <p className="text-xs text-muted-foreground mt-3">{selectedStaff.personal_days?.length || 0} total</p>
                      </div>

                      {/* Documents Section */}
                      <div className="border rounded-lg p-4">
                        <h3 className="font-semibold mb-4 flex items-center gap-2">
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                            <polyline points="14,2 14,8 20,8"/>
                          </svg>
                          Documents
                        </h3>

                        {/* Upload Section */}
                        <div className="flex gap-2 mb-4">
                          <Select value={docType} onValueChange={setDocType}>
                            <SelectTrigger className="w-48">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="teaching_license">Teaching License</SelectItem>
                              <SelectItem value="drivers_license">Driver's License</SelectItem>
                              <SelectItem value="hiring_paperwork">Hiring Paperwork</SelectItem>
                              <SelectItem value="other">Other Document</SelectItem>
                            </SelectContent>
                          </Select>
                          <input
                            type="file"
                            ref={fileInputRef}
                            onChange={handleUploadDocument}
                            className="hidden"
                            accept="image/*,.pdf,.doc,.docx"
                          />
                          <Button
                            onClick={() => fileInputRef.current?.click()}
                            disabled={uploadingDoc}
                            className="bg-[#00305E]"
                          >
                            {uploadingDoc ? "Uploading..." : "Upload Document"}
                          </Button>
                        </div>

                        {/* Documents List */}
                        {staffDocuments.length > 0 ? (
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Document Type</TableHead>
                                <TableHead>Filename</TableHead>
                                <TableHead>Uploaded</TableHead>
                                <TableHead className="text-right">Actions</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {staffDocuments.map((doc) => (
                                <TableRow key={doc.id}>
                                  <TableCell>
                                    <Badge variant="outline">{getDocTypeLabel(doc.document_type)}</Badge>
                                  </TableCell>
                                  <TableCell className="font-medium">{doc.filename}</TableCell>
                                  <TableCell>{formatDate(doc.uploaded_at)}</TableCell>
                                  <TableCell className="text-right">
                                    <Button variant="ghost" size="sm" onClick={() => handleViewDocument(doc)}>
                                      View
                                    </Button>
                                    <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDeleteDocument(doc.id)}>
                                      Delete
                                    </Button>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        ) : (
                          <p className="text-center text-muted-foreground py-4">No documents uploaded</p>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center h-64 text-muted-foreground">
                      <div className="text-center">
                        <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" className="mx-auto mb-4 opacity-50">
                          <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
                          <circle cx="9" cy="7" r="4"/>
                          <path d="M22 21v-2a4 4 0 0 0-3-3.87"/>
                          <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                        </svg>
                        <p>Select a staff member to view their records</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ACCIDENT REPORTS TAB */}
        <TabsContent value="accidents" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-[#A62E1D]">
                      <path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z"/>
                    </svg>
                    Staff Accident Reports
                  </CardTitle>
                  <CardDescription>Confidential injury reports for staff members</CardDescription>
                </div>
                <Badge variant="outline" className="bg-[#A62E1D]/10 text-[#A62E1D] border-[#A62E1D]/30">
                  {filteredReports.length} report{filteredReports.length !== 1 ? 's' : ''}
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="mb-4">
                <Select value={staffFilter} onValueChange={setStaffFilter}>
                  <SelectTrigger className="w-64">
                    <SelectValue placeholder="Filter by staff member" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Staff Members</SelectItem>
                    {[...new Set(accidentReports.map(r => r.subject_name))].sort((a, b) => a.localeCompare(b)).map((name) => (
                      <SelectItem key={name} value={name}>{name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {filteredReports.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <p>No staff accident reports found</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Staff Member</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Place</TableHead>
                      <TableHead>Medical Response</TableHead>
                      <TableHead>Witness</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredReports.map((report) => (
                      <TableRow key={report.id}>
                        <TableCell className="font-medium">{report.subject_name}</TableCell>
                        <TableCell>{formatDate(report.date)}</TableCell>
                        <TableCell>{report.place_of_accident}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={
                            report.medical_response === 'ambulance' ? 'bg-red-100 text-red-800' :
                            report.medical_response === 'first_aid' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-gray-100 text-gray-800'
                          }>
                            {formatMedicalResponse(report.medical_response)}
                          </Badge>
                        </TableCell>
                        <TableCell>{report.witness_name}</TableCell>
                        <TableCell className="text-right">
                          <Button variant="outline" size="sm" data-testid={`view-accident-report-${report.id}`} onClick={() => setSelectedReport(report)}>
                            View Details
                          </Button>
                          <Button variant="ghost" size="sm" className="text-red-600 ml-2" data-testid={`delete-accident-report-${report.id}`} onClick={() => handleDeleteReport(report.id)}>
                            Delete
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* GAPS — lead teachers who recorded no assessment for a block they led (admin) */}
        {isAdmin() && (
        <TabsContent value="gaps" className="space-y-6">
          <Card data-testid="gaps-card">
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <CardTitle>Assessment Gaps</CardTitle>
                  <CardDescription>
                    Lead teachers who did not record any assessment for a block they led.
                    Check the previous day each morning to follow up with anyone who ignored their reminder.
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => shiftGapDate(-1)} data-testid="gaps-prev-day" title="Previous day">
                    <span className="text-lg leading-none">‹</span>
                  </Button>
                  <Input type="date" value={gapDate} onChange={(e) => setGapDate(e.target.value)} className="w-auto h-9" data-testid="gaps-date-input" />
                  <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => shiftGapDate(1)} data-testid="gaps-next-day" title="Next day">
                    <span className="text-lg leading-none">›</span>
                  </Button>
                  <Button variant="outline" size="sm" className="h-9" onClick={() => setGapDate(yesterdayLocal())} data-testid="gaps-yesterday-btn">Yesterday</Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm font-semibold text-slate-700 mb-4" data-testid="gaps-selected-date">{gapPrettyDate}</p>
              {gapsLoading ? (
                <p className="text-sm text-muted-foreground py-8 text-center" data-testid="gaps-loading">Loading gaps…</p>
              ) : !gapsMeta.is_school_day ? (
                <div className="py-10 text-center rounded-lg bg-slate-50 border border-dashed" data-testid="gaps-no-school">
                  <p className="font-medium text-slate-700">No school on this day</p>
                  <p className="text-sm text-muted-foreground mt-1">{gapsMeta.no_school_reason || "Not a school day"}</p>
                </div>
              ) : gaps.length === 0 ? (
                <div className="py-10 text-center rounded-lg bg-emerald-50 border border-emerald-200" data-testid="gaps-empty">
                  <p className="font-semibold text-emerald-700">All assessments complete</p>
                  <p className="text-sm text-emerald-600 mt-1">Every lead teacher recorded their assessments for this day.</p>
                </div>
              ) : (
                <Table data-testid="gaps-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[30%]">Teacher</TableHead>
                      <TableHead>Blocks with gaps (class)</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {gaps.map((g) => (
                      <TableRow key={g.teacher_id} data-testid={`gap-row-${g.teacher_id}`}>
                        <TableCell className="font-semibold text-slate-900 align-top" data-testid={`gap-teacher-${g.teacher_id}`}>{g.teacher_name}</TableCell>
                        <TableCell>
                          <div className="flex flex-col gap-1.5">
                            {g.blocks.map((b) => (
                              <div key={b.block} className="flex flex-wrap items-center gap-2" data-testid={`gap-block-${g.teacher_id}-${b.block.replace(/\s/g, "-")}`}>
                                <Badge variant="outline" className="border-[#A62E1D]/40 text-[#A62E1D] bg-[#A62E1D]/5">{b.block}</Badge>
                                <span className="text-sm text-slate-600">{b.classes.length ? b.classes.join(", ") : "—"}</span>
                              </div>
                            ))}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        )}
      </Tabs>

      {/* Report Detail Dialog */}
      <Dialog open={!!selectedReport} onOpenChange={() => setSelectedReport(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#A62E1D]">
              Staff Accident Report
            </DialogTitle>
          </DialogHeader>
          {selectedReport && (
            <div className="space-y-6">
              <div className="p-4 bg-[#A62E1D]/5 rounded-lg border border-[#A62E1D]/20">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Staff Member</p>
                    <p className="font-semibold text-lg">{selectedReport.subject_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Medical Response</p>
                    <Badge className={
                      selectedReport.medical_response === 'ambulance' ? 'bg-red-500' :
                      selectedReport.medical_response === 'first_aid' ? 'bg-yellow-500' : 'bg-gray-500'
                    }>
                      {formatMedicalResponse(selectedReport.medical_response)}
                    </Badge>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div><p className="text-sm text-muted-foreground mb-1">Date</p><p className="font-medium">{formatDate(selectedReport.date)}</p></div>
                <div><p className="text-sm text-muted-foreground mb-1">Time</p><p className="font-medium">{selectedReport.time || "-"}</p></div>
                <div><p className="text-sm text-muted-foreground mb-1">Place</p><p className="font-medium">{selectedReport.place_of_accident}</p></div>
                <div><p className="text-sm text-muted-foreground mb-1">Witness</p><p className="font-medium">{selectedReport.witness_name}</p></div>
              </div>

              <div>
                <p className="text-sm text-muted-foreground mb-2">Incident Description</p>
                <div className="p-3 bg-muted rounded-lg"><p className="whitespace-pre-wrap">{selectedReport.describe_incident}</p></div>
              </div>

              <div>
                <p className="text-sm text-muted-foreground mb-2">Injury Description</p>
                <div className="p-3 bg-red-50 rounded-lg border border-red-100"><p className="whitespace-pre-wrap">{selectedReport.describe_injury}</p></div>
              </div>

              {selectedReport.follow_up && (
                <div>
                  <p className="text-sm text-muted-foreground mb-2">Follow-up Notes</p>
                  <div className="p-3 bg-blue-50 rounded-lg border border-blue-100"><p className="whitespace-pre-wrap">{selectedReport.follow_up}</p></div>
                </div>
              )}

              <div className="pt-4 border-t flex items-center justify-between">
                <p className="text-sm text-muted-foreground">Report created: {formatDate(selectedReport.created_at)}</p>
                <Button onClick={() => handlePrintReport(selectedReport)} variant="outline" className="flex items-center gap-2">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect width="12" height="8" x="6" y="14"/>
                  </svg>
                  Print Report
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
